import React from "react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import {
    Package, Truck, Tooth, ArrowsClockwise, Wrench, CheckCircle,
    CaretRight, Pulse,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/**
 * CaseActionBanner — status-aware "what to do next" banner for the
 * Care & Production tab.
 *
 * Designed to be the FIRST thing a doctor sees on a case. Replaces the
 * cognitive load of "let me read the status pill and figure out what
 * I'm supposed to do" with one prominent next-action callout.
 *
 * Self-fetches its own shipments via the same /shipments endpoint
 * already used elsewhere — keeps this component drop-in usable from
 * any parent without prop-drilling shipments through every component
 * boundary.
 *
 * Rendering is deliberately deterministic — given the inputs, exactly
 * ONE banner is rendered (the highest-priority action for this case
 * stage). When there's nothing meaningful to surface, returns null so
 * the layout doesn't get an empty hero card.
 */
export default function CaseActionBanner({ caseData: c }) {
    const [shipments, setShipments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!c?.id) return;
        let alive = true;
        api.get(`/cases/${c.id}/shipments`)
            .then((r) => { if (alive) setShipments(r.data || []); })
            .catch(() => { if (alive) setShipments([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [c?.id, c?.status]);

    if (loading || !c) return null;
    const banner = computeBanner(c, shipments);
    if (!banner) return null;

    const ToneIcon = banner.Icon;
    return (
        <div
            className={`rounded-2xl border ${banner.cls} px-5 py-4 flex items-start gap-4 flex-wrap`}
            data-testid="case-action-banner"
            data-banner-kind={banner.kind}
        >
            <div className={`w-12 h-12 rounded-full ${banner.iconBg} flex items-center justify-center shrink-0`}>
                <ToneIcon size={22} weight="fill" className={banner.iconCls} />
            </div>
            <div className="flex-1 min-w-0">
                <div className={`font-display text-base sm:text-lg font-semibold ${banner.titleCls}`}>
                    {banner.title}
                </div>
                <div className="text-sm text-foreground/80 mt-0.5">
                    {banner.subtitle}
                </div>
            </div>
            {banner.cta && (
                <Button
                    type="button"
                    size="sm"
                    onClick={banner.cta.onClick}
                    className={`${banner.cta.cls} shrink-0`}
                    data-testid="case-action-banner-cta"
                    asChild={!!banner.cta.to}
                >
                    {banner.cta.to ? (
                        <Link to={banner.cta.to}>
                            {banner.cta.label} <CaretRight size={14} weight="bold" />
                        </Link>
                    ) : (
                        <>
                            {banner.cta.label} <CaretRight size={14} weight="bold" />
                        </>
                    )}
                </Button>
            )}
        </div>
    );
}


/* ─── decision logic ─────────────────────────────────────────────────── */
// Each branch returns at most one banner. Order matters — earlier branches
// have higher priority.

function computeBanner(c, shipments) {
    if (!c) return null;
    const status = c.status;
    const pendingShipment = shipments.find((s) => s.status !== "received");

    // ── 1. Pending receipt — strongest action signal. If any shipment
    //       is in transit or pending dispatch, confirming receipt
    //       unblocks the entire downstream workflow.
    //   iter101.15 — Added third variant for `delivered`. When the
    //   carrier's tracking webhook flips a shipment to `delivered`
    //   (see backend `manufacturing.py::/webhooks/carrier/tracking`),
    //   we escalate the banner from amber → rose and rewrite the copy
    //   to "The courier says it's at your clinic — please confirm you
    //   got it." The Confirm-Receipt flow itself is UNCHANGED — the
    //   doctor still clicks the same button, this just makes the
    //   moment feel more urgent so it doesn't sit for 14 days waiting
    //   on the auto-confirm cron.
    if (pendingShipment) {
        const range = `Stages ${pendingShipment.stage_from}–${pendingShipment.stage_to}`;
        const isDelivered = pendingShipment.status === "delivered";
        const isInTransit = pendingShipment.status === "in_transit";
        if (isDelivered) {
            return {
                kind: "pending_receipt_delivered",
                Icon: Package,
                cls: "bg-rose-50 border-rose-300 ring-2 ring-rose-200/50",
                iconBg: "bg-rose-100",
                iconCls: "text-rose-700",
                titleCls: "text-rose-900",
                title: `Phase ${pendingShipment.phase_number} · Delivered — please confirm receipt`,
                subtitle: `${range} · The courier reported the package as delivered at your clinic. Open the box, verify contents, and confirm receipt to start treatment.`,
                cta: {
                    label: "Confirm receipt",
                    cls: "bg-rose-600 hover:bg-rose-700 text-white",
                    onClick: () => {
                        const target = document.querySelector(`[data-testid="shipment-row-${pendingShipment.id}"]`);
                        if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
                    },
                },
            };
        }
        return {
            kind: "pending_receipt",
            Icon: isInTransit ? Truck : Package,
            cls: "bg-amber-50 border-amber-200",
            iconBg: "bg-amber-100",
            iconCls: "text-amber-700",
            titleCls: "text-amber-900",
            title: isInTransit
                ? `Phase ${pendingShipment.phase_number} is on its way`
                : `Phase ${pendingShipment.phase_number} is being prepared`,
            subtitle: isInTransit
                ? `${range} · Confirm receipt when the package arrives at your clinic.`
                : `${range} · Your production team is preparing the dispatch. We'll update you when it ships.`,
            // Scroll the doctor down to the Shipments section where the
            // actual "Confirm received" button lives. Keeps the banner
            // simple — one action surface, one entry point.
            cta: isInTransit ? {
                label: "Go to shipment",
                cls: "bg-amber-600 hover:bg-amber-700 text-white",
                onClick: () => {
                    const target = document.querySelector(`[data-testid="shipment-row-${pendingShipment.id}"]`);
                    if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
                },
            } : null,
        };
    }

    // ── 2. Aligners received but not fitted yet — log the fitment.
    if (status === "aligners_received") {
        return {
            kind: "log_fitment",
            Icon: Tooth,
            cls: "bg-primary/5 border-primary/30",
            iconBg: "bg-primary/15",
            iconCls: "text-primary",
            titleCls: "text-foreground",
            title: "Time to fit the aligners",
            subtitle: "Aligners have been received. Schedule the patient's fitting appointment and log it here when done.",
        };
    }

    // ── 3. Active treatment — show day-of-tray + next-switch countdown.
    if (status === "treatment_started" || status === "in_treatment") {
        const stage = Number(c.current_aligner_stage || 0);
        const total = Number(c.manufacturing_spec?.aligner_stages_count || c.total_stages || 0);
        const wearDays = Number(c.plan?.wear_days_per_aligner || c.wear_days_per_aligner || 14);
        const switchedAt = c.aligner_stage_updated_at || c.fitted_at;
        const dayOfTray = computeDayOfTray(switchedAt);
        const daysUntilSwitch = wearDays - dayOfTray;
        return {
            kind: "in_treatment",
            Icon: Pulse,
            cls: "bg-emerald-50 border-emerald-200",
            iconBg: "bg-emerald-100",
            iconCls: "text-emerald-700",
            titleCls: "text-emerald-900",
            title: stage > 0 && total > 0
                ? `Aligner ${stage} of ${total} · Day ${Math.max(1, dayOfTray)} of ${wearDays}`
                : "Treatment in progress",
            subtitle: stage > 0 && daysUntilSwitch > 0
                ? `Next aligner switch in ${daysUntilSwitch} day${daysUntilSwitch === 1 ? "" : "s"}.`
                : stage > 0 && stage === total
                    ? "Patient is on the final aligner. Order retainers when treatment completes."
                    : "Track patient wear time and advance trays in the panel below.",
        };
    }

    // ── 4. Refinement requested — passive informational, planner is working.
    if (status === "refinement_requested") {
        return {
            kind: "refinement_pending",
            Icon: ArrowsClockwise,
            cls: "bg-violet-50 border-violet-200",
            iconBg: "bg-violet-100",
            iconCls: "text-violet-700",
            titleCls: "text-violet-900",
            title: "Refinement requested",
            subtitle: "Your planning team is working on the refinement plan. We'll notify you when the new spec is ready for approval.",
        };
    }

    // ── 5. Case completed — final retainers / completion guidance.
    if (status === "completed") {
        const retainerCount = Number(c.manufacturing_spec?.retainer_count || 0);
        return {
            kind: "completed",
            Icon: CheckCircle,
            cls: "bg-emerald-50 border-emerald-200",
            iconBg: "bg-emerald-100",
            iconCls: "text-emerald-700",
            titleCls: "text-emerald-900",
            title: "Treatment complete",
            subtitle: retainerCount > 0
                ? `Patient has finished active aligner treatment. ${retainerCount} retainer${retainerCount === 1 ? "" : "s"} planned to lock in the result.`
                : "Patient has finished active aligner treatment.",
        };
    }

    // ── 6. Approved or in production (no shipment yet) — informational.
    if (status === "approved" || status === "in_production") {
        return {
            kind: "fabricating",
            Icon: Wrench,
            cls: "bg-slate-50 border-slate-200",
            iconBg: "bg-slate-100",
            iconCls: "text-slate-700",
            titleCls: "text-foreground",
            title: status === "approved" ? "Plan approved — fabrication queued" : "Aligners being fabricated",
            subtitle: "Your lab is producing the aligners. Watch this space — once dispatched, you'll get a confirm-receipt prompt here.",
        };
    }

    // ── Shipment was received but case status hasn't transitioned to
    //    aligners_received yet — uncommon but possible (race between
    //    confirm-receipt API and status update). Just suggest fitting.
    if (status === "shipped" || status === "delivered") {
        return {
            kind: "log_fitment",
            Icon: Package,
            cls: "bg-primary/5 border-primary/30",
            iconBg: "bg-primary/15",
            iconCls: "text-primary",
            titleCls: "text-foreground",
            title: "Aligners received",
            subtitle: "Schedule the patient's fitting appointment and log it here when done.",
        };
    }

    return null;
}


/**
 * Compute "day of current tray" from the stage-switch timestamp. Returns
 * 1 on day 0 (just switched today) so the UI never shows "Day 0 of 14".
 */
function computeDayOfTray(switchedAtIso) {
    if (!switchedAtIso) return 1;
    try {
        const switched = new Date(switchedAtIso);
        const now = new Date();
        const days = Math.floor((now - switched) / (1000 * 60 * 60 * 24));
        return Math.max(1, days + 1);
    } catch {
        return 1;
    }
}
